<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables/wgpt';
import { useRouter } from 'vue-router';

import SnackbarContainer from 'next/wgpt/Snackbar/SnackbarContainer.vue';
import Dialog from 'next/dialog/Dialog.vue';
import AddAutomationForm from './AddAutomationForm.vue';

const emit = defineEmits(['close']);
const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const { t } = useI18n();

const dialogRef = ref(null);
const isOpen = ref(false);
const uiFlags = getters['wgptAutomations/getUIFlags'];

const onClose = () => {
  isOpen.value = false;
  emit('close');
};

const formRef = ref(null);

const open = () => {
  isOpen.value = true;
  nextTick(() => {
    if (!dialogRef.value) return;
    dialogRef.value.open();
  });
};

const close = () => {
  dialogRef.value.close();
  onClose();
};

const handleSubmit = async () => {
  try {
    if (!formRef.value) return;
    const { validate, state } = formRef.value;

    const isFormValid = await validate();
    if (!isFormValid) return;

    const automation = {
      name: state.name,
      description: state.description,
      template: state.template,
      trigger: {},
      steps: {
        v: [],
      },
    };
    const automationRef = await store.dispatch(
      'wgptAutomations/create',
      automation
    );

    useAlert(t('WGPT_AUTOMATIONS.ADD.API.SUCCESS_MESSAGE'));
    close();
    router.push({
      name: 'wgpt_automations_edit',
      params: {
        automationId: automationRef.id,
      },
    });
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('WGPT_AUTOMATIONS.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WGPT_AUTOMATIONS.ADD.TITLE')"
    :description="t('WGPT_AUTOMATIONS.ADD.DESC')"
    :confirm-button-label="t('WGPT_AUTOMATIONS.ADD.FORM.SAVE')"
    :cancel-button-label="t('WGPT_AUTOMATIONS.ADD.FORM.CANCEL')"
    :is-loading="uiFlags.isCreating"
    :disable-confirm-button="uiFlags.isCreating"
    @confirm="handleSubmit"
    @close="onClose"
  >
    <SnackbarContainer class="-top-6 fixed -translate-y-full" />
    <AddAutomationForm v-if="isOpen" ref="formRef" @submit="handleSubmit" />
  </Dialog>
</template>
